import type { OfferingType, OfferingResponse } from '@/types/offering';

/**
 * Pure, side-effect-free duplicate-offering detection for ONE_TO_ONE, GROUP,
 * and CRASH_COURSE teacher offerings.
 *
 * This module trusts whatever `existingOfferings` array it is given — an empty array
 * simply means "no conflicts." It has no knowledge of, and does not handle, fetch
 * failures; that responsibility belongs to the caller (e.g. a wizard component) which
 * must confirm its input data is trustworthy before invoking this function.
 *
 * Every offering type is checked independently — a GROUP offering never conflicts
 * with a ONE_TO_ONE or CRASH_COURSE offering (or vice versa), even if title/subject/
 * grade band/curriculum all match.
 *
 * Grade Band / Curriculum overlap rules differ by type:
 * - ONE_TO_ONE: any overlap with a published, same-subject offering is a conflict
 *   on its own (unconditional).
 * - GROUP: overlap alone is NOT a conflict. A teacher legitimately runs the same
 *   Group class for the same grade band/curriculum across multiple batches (e.g.
 *   Fall vs Spring), differentiated by `nextBatchStartDate`. Overlap is only a
 *   conflict when the candidate ALSO shares the same next batch start date —
 *   i.e. it looks like the same batch was accidentally re-created (commonly via
 *   cloning without updating the date), not a new batch.
 * - CRASH_COURSE: same shape as GROUP, but the differentiator is a compound key —
 *   BOTH `courseStartDate` AND `courseEndDate` must exactly match the candidate's
 *   (not just one of the two) for overlap to become a conflict. A course shifted
 *   by even one of its two dates is treated as a legitimately different run.
 */

export interface DuplicateCheckInput {
  title: string;
  subjectId: string;
  gradeBands: string[];
  curriculumSystems: string[];
  offeringType: OfferingType;
  /** Present in edit mode — this offering excludes itself from comparison. */
  editingOfferingId?: string;
  existingOfferings: OfferingResponse[];
  /**
   * GROUP only — the batch start date (YYYY-MM-DD) the new/edited offering is
   * being published with. Ignored for other types. If omitted for a GROUP
   * offering, no grade-band/curriculum/date conflict is computed (there is
   * nothing to differentiate against) — only the Title check still runs.
   */
  nextBatchStartDate?: string;
  /**
   * CRASH_COURSE only — the course's start date (YYYY-MM-DD). Ignored for other
   * types. Both this and `courseEndDate` must be provided together for the
   * grade-band/curriculum/date conflict to be computed — if either is missing,
   * only the Title check still runs.
   */
  courseStartDate?: string;
  /** CRASH_COURSE only — the course's end date (YYYY-MM-DD). See `courseStartDate`. */
  courseEndDate?: string;
}

export interface DuplicateConflicts {
  title: string | null;
  gradeBands: string[];
  curriculumSystems: string[];
  /** GROUP only — populated when a same-subject, same-date, overlapping published batch exists. */
  nextBatchDate: string[];
  /** CRASH_COURSE only — populated when a same-subject, same-start-AND-end-date, overlapping published course exists. */
  courseDates: string[];
  hasAny: boolean;
}

/**
 * Converts a raw enum value (e.g. "GRADE_5", "STATE_BOARD", "CBSE") into a
 * human-readable label by replacing underscores with spaces and title-casing
 * each word — except short (<=4 char) words, which are treated as acronyms
 * (CBSE, ICSE, IB, AP) and left upper-case. This matches the exact wording
 * required by the requirements doc's own examples: "Grade 5" (title-cased)
 * and "CBSE" (acronym preserved), which a blanket title-case would break by
 * producing "Cbse".
 */
function toLabel(rawValue: string): string {
  return rawValue
    .split('_')
    .map((word) => {
      if (word.length === 0) return word;
      if (word.length <= 4) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/** Strips any time component, comparing dates as plain YYYY-MM-DD strings — avoids Date-object timezone drift entirely. */
function normalizeDate(rawDate: string): string {
  return rawDate.slice(0, 10);
}

/** Formats a YYYY-MM-DD string as e.g. "Jan 15, 2027" without parsing through UTC (which can shift the displayed day). */
function formatDateLabel(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const TYPE_LABELS: Record<string, string> = {
  ONE_TO_ONE: '1:1',
  GROUP: 'Group',
  CRASH_COURSE: 'Crash Course',
};

export function checkOfferingDuplicates(input: DuplicateCheckInput): DuplicateConflicts {
  if (
    input.offeringType !== 'ONE_TO_ONE' &&
    input.offeringType !== 'GROUP' &&
    input.offeringType !== 'CRASH_COURSE'
  ) {
    return { title: null, gradeBands: [], curriculumSystems: [], nextBatchDate: [], courseDates: [], hasAny: false };
  }

  const pool = input.existingOfferings.filter(
    (o) => o.offeringType === input.offeringType && o.id !== input.editingOfferingId,
  );

  let title: string | null = null;
  const normalizedInputTitle = input.title.trim().toLowerCase();
  for (const candidate of pool) {
    if (candidate.title.trim().toLowerCase() === normalizedInputTitle) {
      title = `You already have a ${TYPE_LABELS[input.offeringType]} offering titled '${candidate.title}'. Titles must be unique.`;
      break;
    }
  }

  const publishedSameSubject = pool.filter(
    (o) => o.status === 'PUBLISHED' && o.subjectId === input.subjectId,
  );

  const gradeBandConflicts: string[] = [];
  const curriculumConflicts: string[] = [];
  const nextBatchDateConflicts: string[] = [];
  const courseDateConflicts: string[] = [];

  if (input.offeringType === 'ONE_TO_ONE') {
    for (const gradeBand of input.gradeBands) {
      const conflicts = publishedSameSubject.some((o) => o.gradeBands.includes(gradeBand));
      if (conflicts) {
        gradeBandConflicts.push(
          `${toLabel(gradeBand)} is already included in another published offering for this subject.`,
        );
      }
    }

    for (const curriculum of input.curriculumSystems) {
      const conflicts = publishedSameSubject.some((o) => o.curriculumSystems.includes(curriculum));
      if (conflicts) {
        curriculumConflicts.push(
          `${toLabel(curriculum)} curriculum is already included in another published offering for this subject.`,
        );
      }
    }
  } else if (input.offeringType === 'GROUP' && input.nextBatchStartDate) {
    // GROUP: overlap is only a conflict when paired with a matching batch date.
    const normalizedInputDate = normalizeDate(input.nextBatchStartDate);
    const hasConflict = publishedSameSubject.some((candidate) => {
      const candidateDate = candidate.group?.nextBatchStartDate;
      if (!candidateDate || normalizeDate(candidateDate) !== normalizedInputDate) return false;
      const gradeOverlap = input.gradeBands.some((g) => candidate.gradeBands.includes(g));
      const curriculumOverlap = input.curriculumSystems.some((c) => candidate.curriculumSystems.includes(c));
      return gradeOverlap || curriculumOverlap;
    });
    if (hasConflict) {
      nextBatchDateConflicts.push(
        `You already have a published Group offering for this subject starting on ${formatDateLabel(normalizedInputDate)} with an overlapping grade band or curriculum.`,
      );
    }
  } else if (input.offeringType === 'CRASH_COURSE' && input.courseStartDate && input.courseEndDate) {
    // CRASH_COURSE: overlap is only a conflict when the candidate ALSO shares
    // BOTH the same start date AND the same end date (a compound key) — a
    // course shifted by either date alone is a legitimately different run.
    const normalizedInputStart = normalizeDate(input.courseStartDate);
    const normalizedInputEnd = normalizeDate(input.courseEndDate);
    const hasConflict = publishedSameSubject.some((candidate) => {
      const candidateStart = candidate.crashCourse?.courseStartDate;
      const candidateEnd = candidate.crashCourse?.courseEndDate;
      if (!candidateStart || !candidateEnd) return false;
      if (normalizeDate(candidateStart) !== normalizedInputStart) return false;
      if (normalizeDate(candidateEnd) !== normalizedInputEnd) return false;
      const gradeOverlap = input.gradeBands.some((g) => candidate.gradeBands.includes(g));
      const curriculumOverlap = input.curriculumSystems.some((c) => candidate.curriculumSystems.includes(c));
      return gradeOverlap || curriculumOverlap;
    });
    if (hasConflict) {
      courseDateConflicts.push(
        `You already have a published Crash Course offering for this subject running from ${formatDateLabel(normalizedInputStart)} to ${formatDateLabel(normalizedInputEnd)} with an overlapping grade band or curriculum.`,
      );
    }
  }

  return {
    title,
    gradeBands: gradeBandConflicts,
    curriculumSystems: curriculumConflicts,
    nextBatchDate: nextBatchDateConflicts,
    courseDates: courseDateConflicts,
    hasAny:
      title !== null ||
      gradeBandConflicts.length > 0 ||
      curriculumConflicts.length > 0 ||
      nextBatchDateConflicts.length > 0 ||
      courseDateConflicts.length > 0,
  };
}
